import { CircularProgress, ClickAwayListener, Drawer, DrawerProps, Stack } from '@mui/material';
import React, { useEffect, Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { fullSize } from '../../../Constants';
import { ProjectSidepanel, setActiveSidepanel } from '../../../redux/project/sliceProjectSidepanel';
import { IAppReduxStore } from '../../../redux/store';
import DocTable from '../../General/DocTable/DocTable';
import AppSidepanel from '../../General/Sidepanels/AppSidepanel';
import ProjectViewSidebar from './ProjectViewSidebar';
import SelectedDocumentsIndicator from './SelectedDocumentsIndicator';
import { useEnqueueSnackbar } from '../../../hooks/useEnqueueSnackbar';
import { ToastSeverity } from '../../General/Toast/Toast';
import { resetAdvanceExportDownloadedDocuments } from '../../../redux/project/documents/sliceProjectAdvancedExportDialog';
import {
    resetDownloadDocumentsError,
    resetDownloadZipError,
    resetNumberOfDownloadedDocuments,
    setShowSingleDocumentLoading,
} from '../../../redux/project/file/sliceProjectFileDownload';
import {
    closeRemoveAdditionalDocDialog,
    resetRemoveAdditionalDocument,
    resetRemoveAdditionalDocumentsError,
} from '../../../redux/project/documents/sliceProjectAdditionalDocuments';
import {
    fetchDocuments,
    fetchViewData,
    fetchViewDataAndDocuments,
} from '../../../util/ProjectUtils';
import { useParams } from 'react-router-dom';
import {
    closeDocumentDetailsDialog,
    resetErrorSaveForm,
} from '../../../redux/project/documents/sliceProjectDocumentDetails';
import { filingViewKeysMapper } from '../../General/DocTable/DocTableActions';
import {
    resetAfterDownloadingReports,
    resetDownloadedReportsErrorState,
    resetRedliningReportState,
} from '../../../redux/project/documents/sliceProjectReportTemplate';
import { resetExportToCSVError } from '../../../redux/project/documents/sliceProjectDocumentsExportToCSV';
import { resetCustomColumnsViewError } from '../../../redux/project/sliceProjectViewData';
import { resetErrorUploadFilesGeneral } from '../../../redux/general/sliceFileUpload';
import { resetErrorPostProjectAccessLog } from '../../../redux/general/sliceDatabaseAccess';
import { resetErrorDynamicAction } from '../../../redux/project/documents/sliceProjectDocumentsDynamicSelection';
import { resetCreateNoticeErrorState } from '../../../redux/project/documents/sliceProjectDocumentsCreateNotice';
import {
    isICategorizationSuccess,
    resetCategorizationState,
} from '../../../redux/general/sliceCategorization';
import { resetGetProjectUsersErrorState } from '../../../redux/project/sliceProjectUserList';
import {
    closeSaveAssemblyDialog,
    resetErrorSaveAssembly,
    resetSaveAssembly,
} from '../../../redux/project/documents/sliceProjectAssembly';
import { resetChangeReleaseStatusErrorState } from '../../../redux/project/documents/sliceProjectAdditionalDocumentChangeReleaseStatus';
import {
    resetIsSignatureSaved,
    resetUserSignatureError,
} from '../../../redux/project/sliceProjectUserSignatures';
import {
    resetVdcViewerUpload,
    resetErrorVdcViewerUpload,
    resetLoadingVdcViewerUpload,
    resetErrorVdcViewerRemove,
    resetLoadingVdcViewerRemove,
    resetVdcViewerRemove,
} from '../../../redux/project/documents/sliceProjectVdcCollaboration';

/** Props for the Sidepanel Drawer. */
const drawerProps: DrawerProps = {
    PaperProps: { style: { position: 'absolute' }, elevation: 3 },
    variant: 'permanent',
    anchor: 'right',
};

/**Create a new instance of BroadcastChannel to communicate between different documents */
const bc = new BroadcastChannel('project_view');

/**
 * Component that shows the content of the ProjectView.
 */
export default ({ setShowSelectedDocs }: { setShowSelectedDocs: (t: boolean) => void }) => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'projectView' });

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    const params = useParams();

    /** Access relevant state variables from the redux store. */
    const numberOfDownloadedDocuments = useSelector(
        (_store: IAppReduxStore) => _store.projectFileDownload.numberOfDownloadedDocuments,
    );
    const showSingleDocumentLoading = useSelector(
        (_store: IAppReduxStore) => _store.projectFileDownload.showSingleDocumentLoading,
    );
    const errorDownload = useSelector(
        (store: IAppReduxStore) => store.projectFileDownload.downloadDocuments.error,
    );
    const errorDownloadZip = useSelector(
        (store: IAppReduxStore) => store.projectFileDownload.downloadZip.error,
    );
    const errorExportToCSV = useSelector(
        (store: IAppReduxStore) => store.projectDocumentsExportToCSV.error,
    );
    const areasLoading = useSelector((store: IAppReduxStore) => store.projectAreas.loading);
    const viewsLoading = useSelector((store: IAppReduxStore) => store.projectViews.loading);
    const viewStructureLoading = useSelector(
        (store: IAppReduxStore) => store.projectViewData.structure.loading,
    );
    const fieldsCustomError = useSelector(
        (store: IAppReduxStore) => store.projectViewData.fieldsCustom.error,
    );
    const schemaCustomError = useSelector(
        (store: IAppReduxStore) => store.projectViewData.schemaCustom.error,
    );
    const { errorDeleteCustomColumnsView, errorSaveCustomColumnsView } = useSelector(
        (store: IAppReduxStore) => store.projectViewData,
    );
    const openSidepanel = useSelector((store: IAppReduxStore) => store.projectSidepanel.active);
    const {
        isSingleProtocolDocumentExported,
        downloadedDocuments: downloadedAdvanceExportDocuments,
        error: advancedExportDocumentsError,
    } = useSelector((_store: IAppReduxStore) => _store.projectAdvancedExportDialog);
    const { downloadedReports, isReportCreated: isReportCreatedFromTemplate } = useSelector(
        (_store: IAppReduxStore) => _store.projectDocumentsReportFromTemplate,
    );
    const errorDownloadingReports = useSelector(
        (_store: IAppReduxStore) => _store.projectDocumentsReportFromTemplate.error,
    );
    const errorUploadFilesGeneral = useSelector((store: IAppReduxStore) => store.fileUpload.error);
    const {
        docTypeKeyForTemplateCreation: docTypeKey,
        isDocumentCreated,
        errorSaveForm,
    } = useSelector((store: IAppReduxStore) => store.projectDocumentDetails);

    const { data: isAdditionalDocumentRemoved, error: errorRemoveAdditionalDocument } = useSelector(
        (store: IAppReduxStore) => store.projectAdditionalDocs.isAdditionalDocumentRemoved,
    );

    const { error: errorDatabaseAccess } = useSelector(
        (store: IAppReduxStore) => store.databaseAccess,
    );
    const { redliningReportState } = useSelector(
        (store: IAppReduxStore) => store.projectDocumentsReportFromTemplate,
    );
    const { error: errorDynamicAction } = useSelector(
        (store: IAppReduxStore) => store.projectDocumentsDynamicSelection,
    );
    const { error: errorCreateNotice } = useSelector(
        (store: IAppReduxStore) => store.projectDocumentsCreateNotice,
    );
    const { error: errorCreateAssembly, data: dataCreateAssembly } = useSelector(
        (store: IAppReduxStore) => store.projectSaveAssembly,
    );
    const { error: errorChangeAdditionalDocumentReleaseStatus } = useSelector(
        (store: IAppReduxStore) => store.projectDocumentChangeReleaseStatus,
    );
    const {
        error: errorVdcDocumentUpload,
        data: successVdcDocumentUpload,
        loading: loadingVdcDocumentUpload,
    } = useSelector((store: IAppReduxStore) => store.projectVdcCollaboration.vdcViewerUploadState);
    const {
        error: errorVdcDocumentRemove,
        data: successVdcDocumentRemove,
        loading: loadingVdcDocumentRemove,
    } = useSelector((store: IAppReduxStore) => store.projectVdcCollaboration.vdcViewerRemoveState);

    const { categorizationState } = useSelector((_store: IAppReduxStore) => _store.categorization);
    const { error: errorProjectUserList } = useSelector(
        (store: IAppReduxStore) => store.projectUserList,
    );

    const { error: errorSavingUserSignature, isSignatureSaved: isUserSignatureSaved } = useSelector(
        (store: IAppReduxStore) => store.projectUserSignatureSettings,
    );

    /** Hook for customizing Snackbar. */
    const enqueueSnackbar = useEnqueueSnackbar();

    useEffect(() => {
        if (isICategorizationSuccess(categorizationState)) {
            enqueueSnackbar(
                t(`categorizationSuccess`, {
                    count: categorizationState.docCount,
                }),
                {
                    severity: ToastSeverity.Success,
                },
            );
            dispatch(resetCategorizationState());

            fetchViewDataAndDocuments(params);
        }
    }, [categorizationState]);

    useEffect(() => {
        if (isUserSignatureSaved) {
            enqueueSnackbar(t(`userSignatureSuccess`), {
                severity: ToastSeverity.Success,
            });
            dispatch(resetIsSignatureSaved());
        }
    }, [isUserSignatureSaved]);

    const redliningReportStateCallbacks = {
        Pending: () => {
            enqueueSnackbar(t('toastProtocolExportDocumentInfo'));
        },
        Succeed: () => {
            enqueueSnackbar(t('toastAdvanceExportSuccess', { numberOfDocuments: 1 }), {
                severity: ToastSeverity.Success,
            });
        },
        Failed: () => {
            enqueueSnackbar(t('errorRedliningReport'), {
                severity: ToastSeverity.Error,
            });
        },
        NoRedlinings: () => {
            enqueueSnackbar(t('infoNoRedlinings'));
        },
    };

    useEffect(() => {
        const callback = redliningReportStateCallbacks[redliningReportState];
        callback?.();

        if (redliningReportState !== 'Pending') {
            dispatch(resetRedliningReportState());
        }
    }, [redliningReportState]);

    /** Display open documents errors as the queue of Snackbars. */
    useEffect(() => {
        const onmessage = (ev: MessageEvent<string>) => {
            enqueueSnackbar(t('errorViewDocument'), {
                additionalInfo: ev.data,
                severity: ToastSeverity.Error,
            });
        };
        bc.addEventListener('message', onmessage);
        return () => bc.removeEventListener('message', onmessage);
    }, []);

    /** Display download errors as the queue of Snackbars. */
    useEffect(() => {
        if (errorDownload) {
            enqueueSnackbar(t('fileDownloadError'), {
                additionalInfo: errorDownload.message,
                severity: ToastSeverity.Error,
            });
            dispatch(resetDownloadDocumentsError());
        }
        if (errorDownloadZip) {
            enqueueSnackbar(t('zipDownloadError'), {
                additionalInfo: errorDownloadZip.message,
                severity: ToastSeverity.Error,
            });
            dispatch(resetDownloadZipError());
        }
        if (errorExportToCSV) {
            enqueueSnackbar(t('exportToCSVError'), {
                additionalInfo: errorExportToCSV.message,
                severity: ToastSeverity.Error,
            });
            dispatch(resetExportToCSVError());
        }
        if (errorSaveForm) {
            enqueueSnackbar(t('errorSaveForm'), {
                severity: ToastSeverity.Error,
            });
            dispatch(resetErrorSaveForm());
        }
        if (errorDownloadingReports) {
            enqueueSnackbar(t('toastReportsFromTemplateError'), {
                severity: ToastSeverity.Error,
            });
            dispatch(resetDownloadedReportsErrorState());
        }
        if (fieldsCustomError || schemaCustomError) {
            enqueueSnackbar(t('toastCustomColumnsViewError'), {
                severity: ToastSeverity.Error,
            });
            dispatch(resetCustomColumnsViewError());
        }
        if (errorSaveCustomColumnsView) {
            enqueueSnackbar(t('toastCustomColumnsViewSaveError'), {
                severity: ToastSeverity.Error,
            });
            dispatch(resetCustomColumnsViewError());
        }
        if (errorDeleteCustomColumnsView) {
            enqueueSnackbar(t('toastCustomColumnsViewDeleteError'), {
                severity: ToastSeverity.Error,
            });
            dispatch(resetCustomColumnsViewError());
        }
        if (advancedExportDocumentsError) {
            enqueueSnackbar(t('toastAdvancedExportDocumentsError'), {
                severity: ToastSeverity.Error,
            });
            dispatch(resetAdvanceExportDownloadedDocuments());
        }
        if (errorRemoveAdditionalDocument) {
            enqueueSnackbar(t('errorRemoveAdditionalDocument'), {
                severity: ToastSeverity.Error,
            });
            dispatch(resetRemoveAdditionalDocumentsError());
        }
        if (errorUploadFilesGeneral) {
            enqueueSnackbar(t('errorUploadFilesGeneral'), {
                severity: ToastSeverity.Error,
            });
            dispatch(resetErrorUploadFilesGeneral());
        }
        if (errorDatabaseAccess) {
            enqueueSnackbar(t('errorDatabaseAccess'), {
                severity: ToastSeverity.Error,
            });
            dispatch(resetErrorPostProjectAccessLog());
        }
        if (errorDynamicAction) {
            enqueueSnackbar(t('errorDynamicAction'), {
                severity: ToastSeverity.Error,
            });
            dispatch(resetErrorDynamicAction());
        }
        if (errorCreateNotice) {
            enqueueSnackbar(t('errorCreateNotice'), {
                severity: ToastSeverity.Error,
            });
            dispatch(resetCreateNoticeErrorState());
        }
        if (errorProjectUserList) {
            enqueueSnackbar(t('errorProjectUserList'), {
                severity: ToastSeverity.Error,
            });
            dispatch(resetGetProjectUsersErrorState());
        }
        if (errorCreateAssembly) {
            enqueueSnackbar(t('toastCreateAssemblyError'), {
                severity: ToastSeverity.Error,
            });
            dispatch(resetErrorSaveAssembly());
        }
        if (errorChangeAdditionalDocumentReleaseStatus) {
            enqueueSnackbar(t('errorChangeAdditionalDocumentReleaseStatus'), {
                severity: ToastSeverity.Error,
            });
            dispatch(resetChangeReleaseStatusErrorState());
        }
        if (errorSavingUserSignature) {
            enqueueSnackbar(t('errorSavingUserSignature'), {
                severity: ToastSeverity.Error,
            });
            dispatch(resetUserSignatureError());
        }
        if (errorVdcDocumentUpload) {
            enqueueSnackbar(t('toastVdcUploadError'), {
                severity: ToastSeverity.Error,
            });
            dispatch(resetErrorVdcViewerUpload());
        }
        if (errorVdcDocumentRemove) {
            enqueueSnackbar(t('toastVdcRemoveError'), {
                severity: ToastSeverity.Error,
            });
            dispatch(resetErrorVdcViewerRemove());
        }
    }, [
        errorDownload,
        errorDownloadZip,
        errorExportToCSV,
        errorSaveForm,
        errorDownloadingReports,
        fieldsCustomError,
        schemaCustomError,
        errorDeleteCustomColumnsView,
        errorSaveCustomColumnsView,
        advancedExportDocumentsError,
        errorRemoveAdditionalDocument,
        errorDatabaseAccess,
        errorDynamicAction,
        errorCreateNotice,
        errorProjectUserList,
        errorCreateAssembly,
        errorChangeAdditionalDocumentReleaseStatus,
        errorSavingUserSignature,
        errorVdcDocumentUpload,
        errorVdcDocumentRemove,
    ]);

    useEffect(() => {
        if (isSingleProtocolDocumentExported) {
            enqueueSnackbar(
                t('toastProtocolExportDocumentInfo', {
                    numberOfDocuments: 1,
                }),
                {
                    severity: ToastSeverity.Info,
                },
            );
        }
    }, [isSingleProtocolDocumentExported]);

    useEffect(() => {
        if (downloadedAdvanceExportDocuments) {
            enqueueSnackbar(
                t('toastAdvanceExportSuccess', {
                    numberOfDocuments: downloadedAdvanceExportDocuments,
                }),
                {
                    severity: ToastSeverity.Success,
                },
            );
            dispatch(resetAdvanceExportDownloadedDocuments());
        }
    }, [downloadedAdvanceExportDocuments]);

    useEffect(() => {
        if (isReportCreatedFromTemplate) {
            downloadedReports
                ? enqueueSnackbar(
                      t('toastReportsFromTemplateSuccess', {
                          numberOfDocuments: downloadedReports,
                      }),
                      {
                          severity: ToastSeverity.Success,
                      },
                  )
                : enqueueSnackbar(t('toastDatabaseReportsFromTemplateSuccess'), {
                      severity: ToastSeverity.Success,
                  });

            dispatch(resetAfterDownloadingReports());
        }
    }, [downloadedReports, isReportCreatedFromTemplate]);

    useEffect(() => {
        if (showSingleDocumentLoading) {
            enqueueSnackbar(t('toastDownloadInfo', { numberOfDocuments: 1 }), {
                severity: ToastSeverity.Info,
            });
            dispatch(setShowSingleDocumentLoading(false));
            dispatch(resetNumberOfDownloadedDocuments());
        }
    }, [showSingleDocumentLoading]);

    useEffect(() => {
        if (numberOfDownloadedDocuments) {
            enqueueSnackbar(
                t('toastDownloadSuccess', {
                    numberOfDocuments: numberOfDownloadedDocuments,
                }),
                {
                    severity: ToastSeverity.Success,
                },
            );
            dispatch(resetNumberOfDownloadedDocuments());
        }
    }, [numberOfDownloadedDocuments]);

    useEffect(() => {
        if (isDocumentCreated) {
            const text = Object.values(filingViewKeysMapper).includes(docTypeKey)
                ? 'createFiling'
                : docTypeKey;
            enqueueSnackbar(t(`${text}`), {
                severity: ToastSeverity.Success,
            });
            dispatch(closeDocumentDetailsDialog(false));
        }
    }, [docTypeKey, isDocumentCreated]);

    useEffect(() => {
        if (isAdditionalDocumentRemoved) {
            enqueueSnackbar(t('toastRemoveAdditionalDocumentSuccess'), {
                severity: ToastSeverity.Success,
            });

            fetchDocuments(params);
            fetchViewData(params);

            dispatch(resetRemoveAdditionalDocument());
            dispatch(closeRemoveAdditionalDocDialog());
        }
    }, [isAdditionalDocumentRemoved]);

    useEffect(() => {
        if (dataCreateAssembly) {
            enqueueSnackbar(t('toastCreateAssemblySuccess'), {
                severity: ToastSeverity.Success,
            });

            dispatch(resetSaveAssembly());
            dispatch(closeSaveAssemblyDialog());
        }
    }, [dataCreateAssembly]);

    useEffect(() => {
        const numberOfFailedDocuments = successVdcDocumentUpload?.failedDocuments?.length;

        if (numberOfFailedDocuments > 0) {
            enqueueSnackbar(
                t('toastVdcUploadWarning', {
                    numberOfDocuments: numberOfFailedDocuments,
                }),
                {
                    severity: ToastSeverity.Warning,
                },
            );

            resetVdcViewerUpload();
        } else if (successVdcDocumentUpload) {
            enqueueSnackbar(t('toastVdcUploadSuccess'), {
                severity: ToastSeverity.Success,
            });

            resetVdcViewerUpload();
        }
    }, [successVdcDocumentUpload]);

    useEffect(() => {
        if (loadingVdcDocumentUpload) {
            enqueueSnackbar(t('toastVdcUploadLoading'), {
                severity: ToastSeverity.Info,
            });

            resetLoadingVdcViewerUpload();
        }
    }, [loadingVdcDocumentUpload]);

    useEffect(() => {
        const numberOfFailedDocuments = successVdcDocumentRemove?.failedDocuments?.length;

        if (numberOfFailedDocuments > 0) {
            enqueueSnackbar(
                t('toastVdcRemoveWarning', {
                    numberOfDocuments: numberOfFailedDocuments,
                }),
                {
                    severity: ToastSeverity.Warning,
                },
            );

            resetVdcViewerRemove();
        } else if (successVdcDocumentRemove) {
            enqueueSnackbar(t('toastVdcRemoveSuccess'), {
                severity: ToastSeverity.Success,
            });

            resetVdcViewerRemove();
        }
    }, [successVdcDocumentRemove]);

    useEffect(() => {
        if (loadingVdcDocumentRemove) {
            enqueueSnackbar(t('toastVdcRemoveLoading'), {
                severity: ToastSeverity.Info,
            });

            resetLoadingVdcViewerRemove();
        }
    }, [loadingVdcDocumentRemove]);

    /** Waiting on a request, so show a loading indicator. */
    if (areasLoading || viewsLoading || viewStructureLoading) {
        return (
            <Stack
                sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    ...fullSize,
                }}
            >
                <CircularProgress />
            </Stack>
        );
    }

    return (
        <Stack
            direction='row'
            sx={{
                height: '100%',
                overflow: 'hidden',
            }}
        >
            <ProjectViewSidebar />
            <Stack
                direction='row'
                sx={{ ...fullSize, pl: 2, position: 'relative', overflow: 'hidden' }}
            >
                <Stack
                    sx={{
                        overflow: 'auto',
                        width: '100%',
                        height: '100%',
                    }}
                >
                    <DocTable />
                    <SelectedDocumentsIndicator onClick={() => setShowSelectedDocs(true)} />
                </Stack>
                <ClickAwayListener
                    mouseEvent='onMouseDown'
                    touchEvent='onTouchStart'
                    onClickAway={() => dispatch(setActiveSidepanel(ProjectSidepanel.None))}
                >
                    <Drawer {...drawerProps} open={openSidepanel !== ProjectSidepanel.None}>
                        <AppSidepanel />
                    </Drawer>
                </ClickAwayListener>
            </Stack>
        </Stack>
    );
};
